import {
  ChatEmptyState,
  ChatProvider,
  ChatWidget,
  useChat,
  type StarterCard,
} from "@anter/ai-chat-sdk";
import { Bot, Code2, Cpu, ShieldCheck, Sparkles, X } from "lucide-react";
import { LandingChatAdapter } from "../lib/landing-adapter";
import BookingModal from "./BookingModal";

/**
 * organizationId and chat agent ID are supplied at build/deploy time via
 * Cloudflare Worker environment variables.
 *   • VITE_ORG_ID        → embedded at build time for the ChatProvider
 *   • VITE_CHAT_AGENT_ID → embedded at build time for the chat stream path
 *
 * During local dev set VITE_ORG_ID and VITE_CHAT_AGENT_ID in apps/landing/.env.
 */
const organizationId = (import.meta.env.VITE_ORG_ID as string | undefined) ?? "anter";
const chatAgentId =
  (import.meta.env.VITE_CHAT_AGENT_ID as string | undefined) ?? "ragnt_b0m7MMhR46GXLIzp2tDv1";
const adapter = new LandingChatAdapter(chatAgentId);

function LandingEmptyState() {
  const { sendMessage } = useChat();

  const starterCards: StarterCard[] = [
    {
      icon: <Bot className="w-4 h-4" />,
      iconColor: "#10b981",
      title: "What are managed agents?",
      description: "Learn how Anter hosts, orchestrates, and runs AI agent sessions.",
      prompt: "What are managed agents and how does Anter orchestrate multi-agent systems?",
    },
    {
      icon: <Cpu className="w-4 h-4" />,
      iconColor: "#3b82f6",
      title: "Multi-Agent Routing",
      description: "Understand how transfer and delegation handoffs work in Anter.",
      prompt: "How does multi-agent routing, transfer, and delegation work in Anter?",
    },
    {
      icon: <Code2 className="w-4 h-4" />,
      iconColor: "#8b5cf6",
      title: "Embed Chat SDK",
      description: "See how to integrate @anter/ai-chat-sdk into your web app.",
      prompt: "How do I embed the Anter Chat SDK and wire up a custom ChatAdapter?",
    },
    {
      icon: <ShieldCheck className="w-4 h-4" />,
      iconColor: "#f59e0b",
      title: "Compliance & Security",
      description: "Ask about GRC framework support: SOC 2, ISO 27001, HIPAA & GDPR.",
      prompt: "Which compliance frameworks and security controls does Anter support?",
    },
  ];

  return (
    <ChatEmptyState
      heading="How can Anter help you?"
      subheading="Select a suggested topic below or ask any question to get started."
      starterCards={starterCards}
      onSendMessage={(msg) => void sendMessage(msg)}
    />
  );
}

// Split out from App.tsx and lazy-loaded: pulls in the full ai-chat-sdk
// dependency tree (Radix UI, zod, react-markdown, ...), which dwarfs every
// page bundle combined. Rendered as a sibling of page content, not a wrapper
// around it, so route navigation never waits on this chunk.
export default function ChatWidgetBundle() {
  return (
    <ChatProvider
      organizationId={organizationId}
      adapter={adapter}
      config={{
        theme: "light",
        enableModelSelector: false,
        enableSlashCommands: false,
        enableCommandPalette: false,
        enableFileUpload: false,
        enableSlashFocusShortcut: false,
        enableArtifacts: true,
      }}
      strings={{
        composerPlaceholder: "Ask a question...",
        footerDisclaimer: "AI responses can contain mistakes.",
      }}
    >
      <BookingModal />
      <ChatWidget
        position="bottom-right"
        title="Anter"
        subtitle=""
        brand={<div />}
        emptyState={<LandingEmptyState />}
        onExportArtifact={async () => {}}
        // No "Open full chat" — onNavigate is a no-op, fullChatUrl returns "#"
        fullChatUrl={() => "#"}
        onNavigate={() => undefined}
        trigger={({ open }: { open: boolean }) => (
          <button
            type="button"
            id="ask-anter-trigger"
            className="group relative flex items-center gap-2 rounded-full border border-primary-border/60 bg-bg-secondary/90 px-3 py-2 font-sans text-xs font-semibold text-text shadow-lg backdrop-blur-md transition-all duration-300 hover:scale-105 hover:border-primary/80 hover:bg-bg-hover hover:shadow-primary-dim focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg cursor-pointer select-none sm:gap-2.5 sm:px-4.5 sm:py-3 sm:text-sm"
            aria-label={open ? "Close chat" : "Ask Anter"}
          >
            {/* Dynamic icon selection */}
            <div className="relative flex items-center justify-center w-4 h-4">
              {open ? (
                <X className="w-4 h-4 text-text transition-transform duration-300 rotate-0 group-hover:rotate-90" />
              ) : (
                <>
                  <Sparkles className="w-4 h-4 text-primary transition-transform duration-500 group-hover:rotate-12 group-hover:scale-110" />
                  {/* Soft breathing ring around the icon when idle */}
                  <span className="absolute inset-0 rounded-full border border-primary/40 animate-ping opacity-75" />
                </>
              )}
            </div>

            {/* Button Text */}
            <span className="tracking-wide">{open ? "Close" : "Ask Anter"}</span>

            {/* Subtle green active dot */}
            {!open && (
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green/75 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green"></span>
              </span>
            )}
          </button>
        )}
      />
    </ChatProvider>
  );
}
